import { observer } from "mobx-react";
import { Checkbox, Form, Input, Modal, Select, Tag } from "antd";
import { translateKey as t } from "../../../../../../locales/translation";
import { ClientsController } from "../../../ClientsController";
import { AppStore } from "~/stores/AppStore";
import { useEffect, useMemo, useRef, useState } from "react";
import { extractErrorMessage, getCountryFlag } from "../../../../../../utils/helpers";
import { debounce } from "lodash";
import ClientWizard from "../../wizard/ClientWizard";

const NewClientModal = observer(({ store, controller }: { store?: AppStore; controller?: ClientsController }) => {
  const [form] = Form.useForm();
  const [clientRefStatus, setClientRefStatus] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [clientData, setClientData] = useState(null);
  const latestResolveRef = useRef<(value: unknown) => void>();
  const latestRejectRef = useRef<(reason?: any) => void>();

  const isAdmin = store.user?.roles?.includes("ADMIN");

  useEffect(() => {
    store.clientSpecificMode = false;
  });

  useEffect(() => {
    if (controller.newClientModal.visible && isAdmin) {
      controller.loadPartners();
    }
  }, [controller.newClientModal.visible, isAdmin, controller]);

  const handleCreateClient = async (form, overwrite) => {
    const formData = form.getFieldsValue();
    console.log("CREATE CLIENT FORM", formData);

    // Store client data and show wizard
    setClientData(formData);
    controller.setNewClientModal("visible", false);
    setShowWizard(true);
  };

  const handleWizardComplete = async (wizardConfig) => {
    console.log("WIZARD CONFIG", wizardConfig);

    store.setPreLoaderProps({
      loading: true,
      loadingText: "Creating Client with Configuration...",
    });

    try {
      // Combine client data with wizard config
      const fullConfig = {
        ...clientData,
        wizardConfig,
      };

      await controller.createClientWithWizard(fullConfig);

      // Reset states
      setShowWizard(false);
      setClientData(null);
      form.resetFields();

      store.setPreLoaderProps({
        loading: false,
        loadingText: "",
      });

      store.displayNotification("Client created successfully!", "success", true);
    } catch (err) {
      store.setPreLoaderProps({
        loading: false,
        loadingText: "",
      });

      // PRO-84: surface the API's own message (e.g. PARTNER_NOT_ALLOWED_ON_STACK) instead of a generic text.
      store.displayNotification(t("lblFailedToCreateClient", "Failed to Create Client"), "error", true, extractErrorMessage(err) || err?.message || null);
    }
  };

  const handleWizardCancel = () => {
    setShowWizard(false);
    setClientData(null);
    controller.setNewClientModal("visible", true);
  };

  const handleOK = () => {
    form
      .validateFields()
      .then(() => {
        handleCreateClient(form, false);
      })
      .catch(() => {});
  };

  const handleCancel = () => {
    form.resetFields();
    controller.setNewClientModal("visible", false);
  };

  const checkClientRef = async (clientRef: string): Promise<boolean> => {
    // Simulate API call
    return store.getData(`clients/validate?ref=${clientRef}`, "pro_client", false);
    //return store.getData(`geocoding/clients/validate`, "pro_client", false);
  };

  // Debounced API call wrapper
  const debouncedCheck = useMemo(
    () =>
      debounce(async (value: string) => {
        try {
          const clientRefExists = await checkClientRef(value);
          setClientRefStatus(clientRefExists ? "error" : "success");

          if (!clientRefExists) {
            latestResolveRef.current?.(true);
          } else {
            latestRejectRef.current?.(new Error("Client Ref is already taken"));
          }
        } catch (err) {
          setClientRefStatus("error");
          latestRejectRef.current?.(new Error("Validation failed. Please try again."));
        }
      }, 1500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const validateClientRef = (_: any, value: string) => {
    setClientRefStatus("");

    if (value.length < 4) {
      setClientRefStatus("error");
      return Promise.reject(new Error("Client Ref must be at least 4 characters long"));
    }
    if (value.length > 14) {
      setClientRefStatus("error");
      return Promise.reject(new Error("Client Ref must be at most 14 characters long"));
    }
    if (!/^[A-Z-]+$/.test(value)) {
      setClientRefStatus("error");
      return Promise.reject(new Error("Client Ref may only contain letters A-Z and dashes"));
    }

    return new Promise((resolve, reject) => {
      latestResolveRef.current = resolve;
      latestRejectRef.current = reject;
      setClientRefStatus("validating");
      debouncedCheck(value);
    });
  };

  const renderClientRefInput = (validateClientRefParam) => {
    return (
      <Form.Item
        className="w-full block mb-4"
        label="Client Ref"
        name="clientRef"
        rules={[{ required: true, message: "Please enter a client reference" }, { validator: validateClientRefParam }]}
        validateStatus={clientRefStatus}
        hasFeedback
      >
        <Input
          placeholder="Enter client reference"
          onInput={(e: any) => {
            e.target.value = e?.target?.value.toUpperCase();
          }}
          // Prevent form submission of space key
          onKeyDown={(e: any) => {
            if (e.key.includes(" ")) {
              e.preventDefault();
            }
          }}
        />
      </Form.Item>
    );
  };

  const renderForm = () => {
    const createDemoClientsOnly = store.user?.permissions.some((p) => p.ref === "restrictDemoClientCreation" && p.current);

    // PRO-84: partners served by another OpenFleet stack are shown but cannot be selected.
    const partnerNotInEnvironmentText = t("lblPartnerNotInCurrentEnvironment", "Served by another OpenFleet environment — log in as that partner to create its clients");
    const noPartnersForEnvironment = isAdmin && controller?.partners?.length > 0 && controller.partners.every((p) => !p.isCurrentEnvironment);

    return (
      <div className="flex justify-center items-center">
        <Form form={form} className="w-full max-w-md" layout={"vertical"}>
          {renderClientRefInput(validateClientRef)}

          {isAdmin && (
            <>
              <Form.Item
                className="w-full block mb-4"
                label="Partner"
                name="partnerId"
                rules={[{ required: true, message: t("lblPartnerIsRequired", "Partner is required") }]}
              >
                <Select
                  showSearch
                  style={{ width: "100%", textAlign: "left" }}
                  placeholder={t("lblSelectAPartner", "Select a partner")}
                  optionFilterProp="children"
                >
                  {controller?.partners?.map((p) => (
                    <Select.Option key={p._id} value={p._id} disabled={!p.isCurrentEnvironment} title={!p.isCurrentEnvironment ? partnerNotInEnvironmentText : undefined}>
                      <div className="flex justify-between">
                        <div>{p.name}</div>
                        <div>
                          <Tag color={p.isCurrentEnvironment ? "green" : "default"} className="mr-1" title={p.isCurrentEnvironment ? t("lblCurrentEnvironment", "Current environment") : partnerNotInEnvironmentText}>
                            {p.environmentHost || t("lblUnknownEnvironment", "Unknown environment")}
                          </Tag>
                          <Tag color="blue" className="mr-0">
                            {p.clientRef || p.ref}
                          </Tag>
                        </div>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              {noPartnersForEnvironment && (
                <div className="mb-4 text-xs text-gray-500 text-left">{t("lblNoPartnersForThisEnvironment", "None of the partners are served by the OpenFleet environment you are logged in to")}</div>
              )}
            </>
          )}

          <Form.Item
            className="w-full block mb-4"
            label="Client Name"
            name="name"
            rules={[{ required: true, message: "Please enter a client name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item className="w-full block mb-4" label="Vertical" name="vertical">
            <Select showSearch={true} style={{ width: "100%", textAlign: "left" }} placeholder="Select a vertical">
              {controller?.verticals?.map((vertical) => {
                return (
                  <Select.Option key={vertical.ref} value={vertical.ref}>
                    <div className="flex justify-between">
                      <div>{vertical.description}</div>
                      <div>
                        <Tag color={vertical.color} className="mr-0 w-16 text-center">
                          {vertical.ref}
                        </Tag>
                      </div>
                    </div>
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item className="w-full block mb-4" label="Country" name="country">
            <Select showSearch={true} style={{ width: "100%", textAlign: "left" }} placeholder="Select a country">
              {controller?.countries?.map((country) => {
                return (
                  <Select.Option key={country.ref} value={country.ref}>
                    <div className="flex">
                      <img alt={"flag"} style={{ width: 20, marginRight: 10 }} src={getCountryFlag(country.code)} />
                      <div className="flex-1">{country.name}</div>
                      <div>
                        <Tag color="blue" className="mr-0 w-16 text-center">
                          {country.ref}
                        </Tag>
                      </div>
                    </div>
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item className="w-full block mb-4 mt-6" name="demo" valuePropName="checked" initialValue={createDemoClientsOnly}>
            <Checkbox disabled={createDemoClientsOnly}>Demo Client</Checkbox>
          </Form.Item>
        </Form>
      </div>
    );
  };

  return (
    <>
      <Modal
        title="New Client"
        open={controller.newClientModal.visible}
        cancelText={t("lblCancel", "Cancel")}
        okText={t("lblNext", "Next")}
        onCancel={handleCancel}
        centered={true}
        okType="primary"
        onOk={handleOK}
      >
        {renderForm()}
      </Modal>

      {showWizard && clientData && <ClientWizard store={store} controller={controller} visible={showWizard} clientData={clientData} onComplete={handleWizardComplete} onCancel={handleWizardCancel} />}
    </>
  );
});

export default NewClientModal;
